import React, { useEffect, useRef, useCallback } from 'react';
import { Hands } from '@mediapipe/hands';
import { Camera } from '@mediapipe/camera_utils';

const GestureDetector = ({ onGesture, onError }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const handsRef = useRef(null);
  const cameraRef = useRef(null);
  const lastGestureTime = useRef(0);
  const gestureHistory = useRef([]);

  // Gesture detection logic
  const detectGesture = useCallback((landmarks) => {
    if (!landmarks || landmarks.length === 0) return null;

    const hand = landmarks[0];
    const fingerTips = [4, 8, 12, 16, 20]; // Thumb, Index, Middle, Ring, Pinky tips
    const fingerBases = [3, 6, 10, 14, 18]; // Finger base joints

    // Check if fingers are extended
    const isFingerExtended = (tipIndex, baseIndex) => {
      return hand[tipIndex].y < hand[baseIndex].y;
    };

    const extendedFingers = fingerTips.map((tip, i) => 
      isFingerExtended(tip, fingerBases[i])
    );

    const extendedCount = extendedFingers.filter(Boolean).length;

    // Gesture detection
    let gesture = null;

    // Peace sign - only index and middle fingers extended
    if (extendedCount === 2 && extendedFingers[1] && extendedFingers[2] && 
        !extendedFingers[0] && !extendedFingers[3] && !extendedFingers[4]) {
      gesture = 'peace';
    }
    // Pointing (only index finger extended)
    else if (extendedCount === 1 && extendedFingers[1]) {
      gesture = 'point';
    }
    // Open hand (all fingers extended) - check for swipe
    else if (extendedCount >= 4) {
      const wristX = hand[0].x;
      const indexTipX = hand[8].x; // Index finger tip
      const currentTime = Date.now();
      
      // Use both wrist and index finger for more reliable detection
      const avgX = (wristX + indexTipX) / 2;
      
      // Add current position to history
      gestureHistory.current.push({ x: avgX, time: currentTime });
      
      // Keep only recent history (last 600ms)
      gestureHistory.current = gestureHistory.current.filter(
        entry => currentTime - entry.time < 600
      );

      // Detect swipe with lower threshold and better logic
      if (gestureHistory.current.length >= 3) {
        const recentPoints = gestureHistory.current.slice(-5); // Use last 5 points
        const startPoint = recentPoints[0];
        const endPoint = recentPoints[recentPoints.length - 1];
        const deltaX = endPoint.x - startPoint.x;
        const deltaTime = endPoint.time - startPoint.time;
        
        // Calculate velocity and ensure consistent direction
        const velocity = Math.abs(deltaX) / Math.max(deltaTime, 1);
        const isConsistentDirection = recentPoints.every((point, index) => {
          if (index === 0) return true;
          const prevDelta = point.x - recentPoints[index - 1].x;
          return (deltaX > 0 && prevDelta >= -0.01) || (deltaX < 0 && prevDelta <= 0.01);
        });
        
        // Lower threshold for better sensitivity
        if (Math.abs(deltaX) > 0.06 && velocity > 0.0001 && isConsistentDirection) {
          if (deltaX > 0) {
            gesture = 'swipe_left'; // Hand moving right = go to previous
          } else {
            gesture = 'swipe_right'; // Hand moving left = go to next
          }
          // Clear history after detecting swipe
          gestureHistory.current = [];
          console.log(`Swipe detected: ${gesture}, deltaX: ${deltaX.toFixed(3)}, velocity: ${velocity.toFixed(6)}`);
        }
      }
    }
    // Fallback: Simple position-based detection for very open hands
    else if (extendedCount >= 3) {
      const handCenterX = hand[9].x; // Middle finger base (palm center)
      
      // Simple left/right detection based on hand position
      if (handCenterX < 0.3) {
        gesture = 'swipe_right'; // Hand on left side = next
      } else if (handCenterX > 0.7) {
        gesture = 'swipe_left'; // Hand on right side = previous
      }
    }

    return gesture;
  }, []);

  // Process hand detection results
  const onResults = useCallback((results) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
      // Draw hand landmarks for debugging
      for (const landmarks of results.multiHandLandmarks) {
        // Draw connections
        ctx.strokeStyle = 'rgba(100, 181, 246, 0.8)';
        ctx.lineWidth = 2;
        
        // Draw hand skeleton
        const connections = [
          [0, 1], [1, 2], [2, 3], [3, 4], // Thumb
          [0, 5], [5, 6], [6, 7], [7, 8], // Index
          [0, 9], [9, 10], [10, 11], [11, 12], // Middle
          [0, 13], [13, 14], [14, 15], [15, 16], // Ring
          [0, 17], [17, 18], [18, 19], [19, 20], // Pinky
          [5, 9], [9, 13], [13, 17] // Palm
        ];

        connections.forEach(([start, end]) => {
          const startPoint = landmarks[start];
          const endPoint = landmarks[end];
          
          ctx.beginPath();
          ctx.moveTo(startPoint.x * canvas.width, startPoint.y * canvas.height);
          ctx.lineTo(endPoint.x * canvas.width, endPoint.y * canvas.height);
          ctx.stroke();
        });

        // Draw landmarks
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        landmarks.forEach((landmark, index) => {
          ctx.beginPath();
          ctx.arc(
            landmark.x * canvas.width,
            landmark.y * canvas.height,
            index === 8 ? 8 : 4, // Make index finger tip larger
            0,
            2 * Math.PI
          );
          ctx.fill();
        });
      }

      // Detect gesture
      const gesture = detectGesture(results.multiHandLandmarks);
      
      // Draw gesture feedback on canvas
      if (gesture) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.font = '14px Arial';
        ctx.fillText(`Gesture: ${gesture}`, 10, 20);
      }
      
      // Draw hand movement trail for swipe debugging
      if (gestureHistory.current.length > 1) {
        ctx.strokeStyle = 'rgba(255, 255, 0, 0.8)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        gestureHistory.current.forEach((point, index) => {
          const x = point.x * canvas.width;
          const y = canvas.height / 2; // Draw at middle height
          if (index === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });
        ctx.stroke();
      }
      
      // Throttle gesture detection to prevent too many triggers
      const now = Date.now();
      if (gesture && now - lastGestureTime.current > 500) { // Increased throttle
        lastGestureTime.current = now;
        onGesture?.(gesture);
      }
    }
  }, [detectGesture, onGesture]);

  // Initialize MediaPipe
  useEffect(() => {
    let isComponentMounted = true;
    let currentHands = null;
    let currentCamera = null;

    // Add global error handler for MediaPipe CDN issues
    const originalError = window.onerror;
    const originalUnhandledRejection = window.onunhandledrejection;
    
    const handleMediaPipeError = (error) => {
      const errorStr = error.toString ? error.toString() : String(error);
      if (errorStr.includes('mediapipe') || 
          errorStr.includes('hands_solution') ||
          errorStr.includes('cdn.jsdelivr.net') ||
          errorStr.includes('packed_assets') ||
          errorStr.includes('deleted object')) {
        console.error('MediaPipe error detected:', errorStr);
        return true; // Handle the error, don't fallback immediately
      }
      return false;
    };

    window.onerror = (message, source, lineno, colno, error) => {
      if (handleMediaPipeError(message || error)) {
        return true;
      }
      return originalError ? originalError(message, source, lineno, colno, error) : false;
    };

    window.onunhandledrejection = (event) => {
      if (handleMediaPipeError(event.reason)) {
        event.preventDefault();
        return;
      }
      if (originalUnhandledRejection) {
        originalUnhandledRejection(event);
      }
    };

    const initializeMediaPipe = async () => {
      // Clean up any existing instances first
      if (handsRef.current) {
        try {
          handsRef.current.close();
        } catch (e) {
          console.warn('Error closing existing hands instance:', e);
        }
        handsRef.current = null;
      }

      if (cameraRef.current) {
        try {
          cameraRef.current.stop();
        } catch (e) {
          console.warn('Error stopping existing camera:', e);
        }
        cameraRef.current = null;
      }

      const cdnOptions = [
        {
          name: 'JSDelivr CDN',
          locateFile: (file) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/hands@0.4.1675469240/${file}`;
          }
        },
        {
          name: 'UNPKG CDN', 
          locateFile: (file) => {
            return `https://unpkg.com/@mediapipe/hands@0.4.1675469240/${file}`;
          }
        }
      ];

      for (let i = 0; i < cdnOptions.length; i++) {
        if (!isComponentMounted) break;
        
        const cdn = cdnOptions[i];
        console.log(`Trying MediaPipe initialization with ${cdn.name}...`);
        
        try {
          // Create a promise that resolves when MediaPipe is ready
          const initPromise = new Promise(async (resolve, reject) => {
            try {
              if (!isComponentMounted) return reject(new Error('Component unmounted'));
              
              console.log(`Attempting initialization with ${cdn.name}...`);
              
              // Initialize Hands with CDN
              currentHands = new Hands({
                locateFile: cdn.locateFile
              });

              // Set conservative options for better reliability
              currentHands.setOptions({
                maxNumHands: 1,
                modelComplexity: 0, // Use lightest model for fastest loading
                minDetectionConfidence: 0.5,
                minTrackingConfidence: 0.3,
                staticImageMode: false
              });

              currentHands.onResults(onResults);
              
              // Test if the hands instance is working by setting it
              handsRef.current = currentHands;
              
              // Small delay to let MediaPipe settle
              await new Promise(resolve => setTimeout(resolve, 800));
              
              if (!isComponentMounted) return reject(new Error('Component unmounted'));
              
              console.log(`MediaPipe initialized successfully with ${cdn.name}`);
              resolve(currentHands);
            } catch (error) {
              console.error(`MediaPipe initialization failed with ${cdn.name}:`, error);
              reject(error);
            }
          });

          // Set a reasonable timeout for each CDN attempt
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('MediaPipe initialization timeout')), 10000)
          );

          // Wait for either successful init or timeout
          await Promise.race([initPromise, timeoutPromise]);

          // Initialize camera with error handling
          if (videoRef.current && isComponentMounted) {
            try {
              currentCamera = new Camera(videoRef.current, {
                onFrame: async () => {
                  try {
                    if (handsRef.current && videoRef.current && isComponentMounted && typeof handsRef.current.send === 'function') {
                      await handsRef.current.send({ image: videoRef.current });
                    }
                  } catch (frameError) {
                    // Handle deleted object error specifically
                    if (frameError.message && frameError.message.includes('deleted object')) {
                      console.warn('MediaPipe object was deleted, triggering fallback...');
                      if (isComponentMounted && onError) {
                        setTimeout(() => onError(), 100);
                      }
                    } else if (frameError.message && !frameError.message.includes('n is not a function')) {
                      console.warn('Frame processing error:', frameError);
                    }
                  }
                },
                width: 320,
                height: 240,
                facingMode: 'user'
              });

              cameraRef.current = currentCamera;
              await currentCamera.start();
              console.log(`MediaPipe and camera initialized successfully with ${cdn.name}!`);
              return; // Success, exit the loop
            } catch (cameraError) {
              console.error(`Camera initialization error with ${cdn.name}:`, cameraError);
              throw cameraError; // Re-throw to try next CDN
            }
          }
        } catch (error) {
          console.error(`MediaPipe initialization failed with ${cdn.name}:`, error);
          
          // Clean up failed instances
          if (currentHands) {
            try {
              currentHands.close();
            } catch (e) {
              console.warn('Error cleaning up failed hands instance:', e);
            }
            currentHands = null;
          }
          
          if (currentCamera) {
            try {
              currentCamera.stop();
            } catch (e) {
              console.warn('Error cleaning up failed camera:', e);
            }
            currentCamera = null;
          }
          
          // Try next CDN
          if (i === cdnOptions.length - 1) {
            // Last attempt failed, show fallback and trigger onError
            console.error('All MediaPipe CDN options failed, switching to fallback');
            showFallbackMessage();
            if (onError && isComponentMounted) {
              setTimeout(() => onError(), 500);
            }
          }
        }
      }
    };

    const showFallbackMessage = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Gesture detection', canvas.width / 2, canvas.height / 2 - 10);
        ctx.fillText('temporarily unavailable', canvas.width / 2, canvas.height / 2 + 5);
        ctx.fillText('Use mouse/keyboard instead', canvas.width / 2, canvas.height / 2 + 20);
      }
    };

    // Add a delay to ensure DOM is ready
    setTimeout(initializeMediaPipe, 1000);

    // Cleanup
    return () => {
      try {
        // Restore original error handlers
        window.onerror = originalError;
        window.onunhandledrejection = originalUnhandledRejection;
        
        if (cameraRef.current) {
          cameraRef.current.stop();
        }
        if (handsRef.current) {
          handsRef.current.close();
        }
      } catch (cleanupError) {
        console.warn('Cleanup error:', cleanupError);
      }
    };
  }, [onResults, onError]);

  return (
    <div className="gesture-detector">
      <video
        ref={videoRef}
        style={{ display: 'none' }}
        autoPlay
        playsInline
        muted
      />
      <canvas
        ref={canvasRef}
        width={200}
        height={150}
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          border: '2px solid rgba(100, 181, 246, 0.3)',
          borderRadius: '12px',
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000
        }}
      />
    </div>
  );
};

export default GestureDetector;